import { Button } from "@bednbody/ui/components/button";
import { Textarea } from "@bednbody/ui/components/textarea";
import { Input } from "@bednbody/ui/components/input";
import { useState } from "react";
import { computeFuelBracket, isFastTrackSpike, isMileageSubmissionWindow } from "@bednbody/logic";
import { useSubmitMileageRequest, useLastWeekFuelPrice } from "../mileage.hooks";

export function MileageRequestForm() {
  const { data: lastWeekAvg } = useLastWeekFuelPrice();
  const submitRequest = useSubmitMileageRequest();

  const [fuelPrice, setFuelPrice] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [notes, setNotes] = useState("");

  const price = parseFloat(fuelPrice);
  const validPrice = !Number.isNaN(price) && price > 0;
  const bracket = validPrice ? computeFuelBracket(price) : null;
  const fastTrack = validPrice ? isFastTrackSpike(price, lastWeekAvg ?? null) : false;
  const inWindow = isMileageSubmissionWindow(new Date(), fastTrack);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validPrice || !effectiveDate) return;
    await submitRequest.mutateAsync({
      fuelPrice: price,
      requestedEffectiveDate: effectiveDate,
      notes: notes || undefined,
    });
    setFuelPrice("");
    setEffectiveDate("");
    setNotes("");
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 rounded-lg border border-border bg-card p-5"
    >
      <h2 className="font-medium">Submit rate change request</h2>

      <label className="flex flex-col gap-1 text-sm">
        Current fuel price (₱/L)
        <Input
          type="number"
          step="0.01"
          required
          value={fuelPrice}
          onChange={(e) => setFuelPrice(e.target.value)}
        />
      </label>

      {validPrice && (
        <div className="rounded-md bg-background px-3 py-2 text-xs text-muted-foreground">
          Bracket: <span className="font-medium text-foreground">{bracket}</span>
          {fastTrack && (
            <span className="ml-2 rounded-full bg-warning/15 px-2 py-0.5 font-medium text-warning">
              Fast-track: spike of ₱10/L or more this week
            </span>
          )}
        </div>
      )}

      <label className="flex flex-col gap-1 text-sm">
        Requested effective date
        <Input
          type="date"
          required
          value={effectiveDate}
          onChange={(e) => setEffectiveDate(e.target.value)}
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        Notes (optional)
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </label>

      {!inWindow && (
        <p className="text-xs text-warning">
          Rate changes normally submit on Mondays. This isn't a fast-track spike; submitting today
          will still work here, but confirm this is intentional (Phase 4 spec: Monday-only UI + API
          check).
        </p>
      )}

      {submitRequest.isError && (
        <p className="text-sm text-destructive">{(submitRequest.error as Error).message}</p>
      )}

      <Button
        variant="default"
        size="sm"
        type="submit"
        disabled={submitRequest.isPending || !validPrice || !effectiveDate}
      >
        {submitRequest.isPending ? "Submitting…" : "Submit for Executive approval"}
      </Button>
    </form>
  );
}
